import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
} from "react-icons/fi";

import * as NewsService from "../services/newsServices";
import { toAbsolutePublicUrl } from "../services/mediaLibraryServices";

import Reveal from "./ui/Reveal";
import NewsDetailModal from "./../pages/NewsPage/components/NewsDetailModal.jsx";

const FALLBACK_NEWS_IMAGE = "/images/news-placeholder.jpg";

const stripHtml = (html = "") =>
  String(html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const makePlainExcerpt = (html = "", max = 190) => {
  const text = stripHtml(html);

  if (!text) return "";

  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const getNewsDate = (item) =>
  item?.published_at ||
  item?.publishedAt ||
  item?.date_publication ||
  item?.date ||
  item?.createdAt ||
  item?.created_at ||
  null;

const formatDateLabel = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const normalizeNewsItem = (item = {}) => {
  const rawImage = item.image_url || item.imageUrl || item.image || "";
  const image = rawImage ? toAbsolutePublicUrl(rawImage) : FALLBACK_NEWS_IMAGE;

  const contentRaw = item.content || item.description || item.body || "";
  const date = getNewsDate(item);
  const dateLabel = formatDateLabel(date);
  const year = date ? new Date(date).getFullYear() : "";

  return {
    ...item,
    id: item.id,
    title: item.title || item.titre || "Sans titre",
    image,
    excerpt: item.excerpt || makePlainExcerpt(contentRaw),
    contentRaw,
    contentText: makePlainExcerpt(contentRaw, 800),
    dateLabel,
    year,
  };
};

const getVisibleCount = () => {
  if (typeof window === "undefined") return 3;
  if (window.innerWidth >= 1180) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
};

function useVisibleCount() {
  const [visibleCount, setVisibleCount] = useState(getVisibleCount);

  useEffect(() => {
    const onResize = () => {
      setVisibleCount(getVisibleCount());
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return visibleCount;
}

function NewsCard({ item, onOpen, delay = 0 }) {
  return (
    <Reveal delay={delay} className="h-full">
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-950">
        <button
          type="button"
          onClick={() => onOpen?.(item)}
          className="relative block overflow-hidden bg-slate-100 text-left dark:bg-slate-900"
          aria-label={`Lire l’actualité ${item.title}`}
        >
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-105"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_NEWS_IMAGE;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/10 to-transparent" />

          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
            {/* <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#1277B9] shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-sky-300">
              <FiFileText className="h-3.5 w-3.5" />
              Actualité
            </span> */}

            {item.dateLabel && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/75 px-3 py-1 text-xs font-bold text-white shadow-sm backdrop-blur">
                <FiCalendar className="h-3.5 w-3.5" />
                {item.dateLabel}
              </span>
            )}
          </div>
        </button>

        <div className="flex flex-1 flex-col p-5">
          <h3 className="line-clamp-2 text-xl font-bold leading-snug text-slate-950 dark:text-white">
            {item.title}
          </h3>

          <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {item.excerpt || "Aucun contenu renseigné."}
          </p>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <button
              type="button"
              onClick={() => onOpen?.(item)}
              className="group/btn inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40 sm:flex-none"
            >
              Lire l’article
              <FiArrowRight className="h-4 w-4 transition group-hover/btn:translate-x-1" />
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function NewsSkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="aspect-[16/10] animate-pulse bg-slate-200 dark:bg-slate-800" />
      <div className="space-y-4 p-5">
        <div className="h-5 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-5/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>
        <div className="h-11 w-36 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function NewsSection() {
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  const visibleCount = useVisibleCount();
  const touchStartX = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchNews = async () => {
      try {
        const data =
          (typeof NewsService.getLatestNews === "function" &&
            (await NewsService.getLatestNews())) ||
          (typeof NewsService.getNews === "function" &&
            (await NewsService.getNews())) ||
          [];

        if (!mounted) return;

        const filtered = (Array.isArray(data) ? data : [])
          .filter((item) => item?.is_active !== false)
          .map(normalizeNewsItem);

        setRawItems(filtered);
      } catch (error) {
        console.error("Erreur chargement news:", error);

        if (mounted) {
          setRawItems([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => rawItems, [rawItems]);

  useEffect(() => {
    setStartIndex(0);
  }, [visibleCount]);

  const canNavigate = items.length > visibleCount;

  const visibleItems = useMemo(() => {
    if (!items.length) return [];

    if (items.length <= visibleCount) return items;

    return Array.from({ length: visibleCount }).map((_, offset) => {
      const index = (startIndex + offset) % items.length;
      return items[index];
    });
  }, [items, startIndex, visibleCount]);

  const prev = () => {
    if (!items.length) return;

    setStartIndex((current) => (current - 1 + items.length) % items.length);
  };

  const next = () => {
    if (!items.length) return;

    setStartIndex((current) => (current + 1) % items.length);
  };

  useEffect(() => {
    if (!canNavigate || paused || selectedNews) return;

    const timer = window.setInterval(() => {
      next();
    }, 6500);

    return () => window.clearInterval(timer);
  }, [canNavigate, paused, selectedNews, items.length]);

  const onTouchStart = (event) => {
    touchStartX.current = event.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (event) => {
    const start = touchStartX.current;
    const end = event.changedTouches?.[0]?.clientX ?? null;

    if (start == null || end == null) return;

    const delta = end - start;

    if (delta > 45) prev();
    if (delta < -45) next();

    touchStartX.current = null;
  };

  const openNewsDetails = (item) => {
    if (!item) return;

    setSelectedNews(item);
    setPaused(true);
  };

  const closeNewsDetails = () => {
    setSelectedNews(null);
    setPaused(false);
  };

  if (!loading && !items.length) return null;

  return (
    <section
      id="actualites"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="news-home-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-blue-300/10 blur-3xl dark:bg-blue-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                Actualités municipales
              </div>

              <h2
                id="news-home-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Les dernières actualités
              </h2>

              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Retrouvez les informations importantes, les annonces et les
                nouveautés de la commune.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <Link
              to="/actualites"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/40 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:border-sky-500/40 dark:hover:text-sky-300"
            >
              Toutes les actualités
              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {loading
              ? Array.from({ length: visibleCount }).map((_, index) => (
                  <NewsSkeletonCard key={index} />
                ))
              : visibleItems.map((item, index) => (
                  <NewsCard
                    key={`${item.id || item.title}-${startIndex}-${index}`}
                    item={item}
                    onOpen={openNewsDetails}
                    delay={index * 100}
                  />
                ))}
          </div>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-0 top-1/2 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-xl shadow-slate-950/10 transition hover:-translate-x-[55%] hover:bg-slate-50 hover:text-[#1277B9] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/30 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 lg:inline-flex"
                aria-label="Actualité précédente"
              >
                <FiChevronLeft className="h-6 w-6" />
              </button>

              <button
                type="button"
                onClick={next}
                className="absolute right-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-xl shadow-slate-950/10 transition hover:translate-x-[55%] hover:bg-slate-50 hover:text-[#1277B9] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/30 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 lg:inline-flex"
                aria-label="Actualité suivante"
              >
                <FiChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={!canNavigate}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-[#1277B9] disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
              aria-label="Actualité précédente"
            >
              <FiArrowLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={next}
              disabled={!canNavigate}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1277B9] text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Actualité suivante"
            >
              <FiArrowRight className="h-5 w-5" />
            </button>

            <span className="ml-2 text-sm font-bold text-slate-500 dark:text-slate-400">
              {items.length
                ? `${startIndex + 1}/${items.length}`
                : loading
                  ? "Chargement…"
                  : "—"}
            </span>
          </div>

          {items.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              {items.map((item, dotIndex) => (
                <button
                  key={item.id || `${item.title}-${dotIndex}`}
                  type="button"
                  onClick={() => setStartIndex(dotIndex)}
                  aria-label={`Afficher l’actualité ${dotIndex + 1}`}
                  className={`h-2.5 rounded-full transition-all ${
                    dotIndex === startIndex
                      ? "w-9 bg-[#1277B9]"
                      : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedNews && (
        <NewsDetailModal item={selectedNews} onClose={closeNewsDetails} />
      )}
    </section>
  );
}